import { useCallback, useEffect, useMemo, useState } from 'react'
import { Message } from '@arco-design/web-react'
import type { OptionsAPI } from '../useOptions'
import { PRICING_KEYS, parseOptionJSONSafe, type PricingKey } from './pricingKeys'

export const PAGE_SIZE = 10
export const PRICE_SUFFIX = '$/1M tokens'

const EMPTY_CANDIDATE_MODEL_NAMES: string[] = []

export type FilterMode = 'all' | 'unset'

export interface ModelPricingState {
  name: string
  billingMode: 'per-token' | 'per-request'
  fixedPrice: string
  inputPrice: string
  completionPrice: string
  lockedCompletionRatio: string
  completionRatioLocked: boolean
  cachePrice: string
  createCachePrice: string
  imagePrice: string
  audioInputPrice: string
  audioOutputPrice: string
  rawRatios: {
    modelRatio: string
    completionRatio: string
    cacheRatio: string
    createCacheRatio: string
    imageRatio: string
    audioRatio: string
    audioCompletionRatio: string
  }
  hasConflict: boolean
}

const EMPTY_MODEL: ModelPricingState = {
  name: '',
  billingMode: 'per-token',
  fixedPrice: '',
  inputPrice: '',
  completionPrice: '',
  lockedCompletionRatio: '',
  completionRatioLocked: false,
  cachePrice: '',
  createCachePrice: '',
  imagePrice: '',
  audioInputPrice: '',
  audioOutputPrice: '',
  rawRatios: {
    modelRatio: '',
    completionRatio: '',
    cacheRatio: '',
    createCacheRatio: '',
    imageRatio: '',
    audioRatio: '',
    audioCompletionRatio: '',
  },
  hasConflict: false,
}

const NUMERIC_INPUT_REGEX = /^(\d+(\.\d*)?|\.\d*)?$/

const PARSE_OPTION_KEYS = [...PRICING_KEYS, 'CompletionRatioMeta'] as const

export const hasValue = (value: unknown): boolean =>
  value !== '' && value !== null && value !== undefined && value !== false

const toNumericString = (value: unknown): string => {
  if (!hasValue(value) && value !== 0) return ''
  const num = Number(value)
  return Number.isFinite(num) ? String(num) : ''
}

const toNumberOrNull = (value: unknown): number | null => {
  if (!hasValue(value) && value !== 0) return null
  const num = Number(value)
  return Number.isFinite(num) ? num : null
}

const formatNumber = (value: unknown): string => {
  const num = toNumberOrNull(value)
  if (num === null) return ''
  return parseFloat(num.toFixed(12)).toString()
}

const toNormalizedNumber = (value: unknown): number | null => {
  const formatted = formatNumber(value)
  return formatted === '' ? null : Number(formatted)
}

const ratioToBasePrice = (ratio: unknown): string => {
  const num = toNumberOrNull(ratio)
  if (num === null) return ''
  return formatNumber(num * 2)
}

const normalizeCompletionRatioMeta = (rawMeta: unknown) => {
  if (!rawMeta || typeof rawMeta !== 'object' || Array.isArray(rawMeta)) {
    return { locked: false, ratio: '' }
  }
  const meta = rawMeta as Record<string, unknown>
  return {
    locked: Boolean(meta.locked),
    ratio: toNumericString(meta.ratio),
  }
}

const buildModelState = (
  name: string,
  sourceMaps: Record<string, Record<string, unknown>>
): ModelPricingState => {
  const modelRatio = toNumericString(sourceMaps.ModelRatio[name])
  const completionRatio = toNumericString(sourceMaps.CompletionRatio[name])
  const completionRatioMeta = normalizeCompletionRatioMeta(sourceMaps.CompletionRatioMeta?.[name])
  const cacheRatio = toNumericString(sourceMaps.CacheRatio[name])
  const createCacheRatio = toNumericString(sourceMaps.CreateCacheRatio[name])
  const imageRatio = toNumericString(sourceMaps.ImageRatio[name])
  const audioRatio = toNumericString(sourceMaps.AudioRatio[name])
  const audioCompletionRatio = toNumericString(sourceMaps.AudioCompletionRatio[name])
  const fixedPrice = toNumericString(sourceMaps.ModelPrice[name])
  const inputPrice = ratioToBasePrice(modelRatio)
  const inputPriceNumber = toNumberOrNull(inputPrice)
  const audioInputPrice =
    inputPriceNumber !== null && hasValue(audioRatio)
      ? formatNumber(inputPriceNumber * Number(audioRatio))
      : ''

  return {
    ...EMPTY_MODEL,
    name,
    billingMode: hasValue(fixedPrice) ? 'per-request' : 'per-token',
    fixedPrice,
    inputPrice,
    completionRatioLocked: completionRatioMeta.locked,
    lockedCompletionRatio: completionRatioMeta.ratio,
    completionPrice:
      inputPriceNumber !== null &&
      hasValue(completionRatioMeta.locked ? completionRatioMeta.ratio : completionRatio)
        ? formatNumber(
            inputPriceNumber *
              Number(completionRatioMeta.locked ? completionRatioMeta.ratio : completionRatio)
          )
        : '',
    cachePrice:
      inputPriceNumber !== null && hasValue(cacheRatio)
        ? formatNumber(inputPriceNumber * Number(cacheRatio))
        : '',
    createCachePrice:
      inputPriceNumber !== null && hasValue(createCacheRatio)
        ? formatNumber(inputPriceNumber * Number(createCacheRatio))
        : '',
    imagePrice:
      inputPriceNumber !== null && hasValue(imageRatio)
        ? formatNumber(inputPriceNumber * Number(imageRatio))
        : '',
    audioInputPrice,
    audioOutputPrice:
      toNumberOrNull(audioInputPrice) !== null && hasValue(audioCompletionRatio)
        ? formatNumber(Number(audioInputPrice) * Number(audioCompletionRatio))
        : '',
    rawRatios: {
      modelRatio,
      completionRatio,
      cacheRatio,
      createCacheRatio,
      imageRatio,
      audioRatio,
      audioCompletionRatio,
    },
    hasConflict:
      hasValue(fixedPrice) &&
      [modelRatio, completionRatio, cacheRatio, createCacheRatio, imageRatio, audioRatio, audioCompletionRatio].some(
        hasValue
      ),
  }
}

export const isBasePricingUnset = (model: ModelPricingState): boolean =>
  !hasValue(model.fixedPrice) && !hasValue(model.inputPrice)

export const getModelWarnings = (model: ModelPricingState | null): string[] => {
  if (!model) return []
  const warnings: string[] = []
  const hasDerivedPricing = [
    model.inputPrice,
    model.completionPrice,
    model.cachePrice,
    model.createCachePrice,
    model.imagePrice,
    model.audioInputPrice,
    model.audioOutputPrice,
  ].some(hasValue)

  if (model.hasConflict) {
    warnings.push('当前模型同时存在按次价格和倍率配置，保存时会按当前计费方式覆盖。')
  }

  if (
    !hasValue(model.inputPrice) &&
    [
      model.rawRatios.completionRatio,
      model.rawRatios.cacheRatio,
      model.rawRatios.createCacheRatio,
      model.rawRatios.imageRatio,
      model.rawRatios.audioRatio,
      model.rawRatios.audioCompletionRatio,
    ].some(hasValue)
  ) {
    warnings.push('当前模型存在未显式设置输入倍率的扩展倍率；填写输入价格后会自动换算为价格字段。')
  }

  if (model.billingMode === 'per-token' && hasDerivedPricing && !hasValue(model.inputPrice)) {
    warnings.push('按量计费下需要先填写输入价格，才能保存其它价格项。')
  }

  if (
    model.billingMode === 'per-token' &&
    hasValue(model.audioOutputPrice) &&
    !hasValue(model.audioInputPrice)
  ) {
    warnings.push('填写音频补全价格前，需要先填写音频输入价格。')
  }

  return warnings
}

export type ModelStatusLabel = {
  text: string
  variant: 'neutral' | 'info' | 'violet' | 'danger' | 'warning' | 'success'
}

export const getModelStatusLabels = (model: ModelPricingState): ModelStatusLabel[] => {
  const labels: ModelStatusLabel[] = []

  if (model.hasConflict) {
    labels.push({ text: '冲突', variant: 'danger' })
  }

  if (isBasePricingUnset(model)) {
    labels.push({ text: '未设置', variant: 'neutral' })
    return labels
  }

  if (model.billingMode === 'per-request') {
    labels.push({ text: '按次', variant: 'info' })
    return labels
  }

  labels.push({ text: '按量', variant: 'violet' })

  if (model.completionRatioLocked) {
    labels.push({ text: '补全倍率锁定', variant: 'warning' })
  }

  const hasExtended = [
    model.cachePrice,
    model.createCachePrice,
    model.imagePrice,
    model.audioInputPrice,
    model.audioOutputPrice,
  ].some(hasValue)

  if (hasExtended) {
    labels.push({ text: '扩展价格', variant: 'success' })
  }

  return labels
}

export const buildSummaryText = (model: ModelPricingState): string => {
  if (model.billingMode === 'per-request' && hasValue(model.fixedPrice)) {
    return `按次 $${model.fixedPrice} / 次`
  }
  if (hasValue(model.inputPrice)) {
    const extraCount = [
      model.completionPrice,
      model.cachePrice,
      model.createCachePrice,
      model.imagePrice,
      model.audioInputPrice,
      model.audioOutputPrice,
    ].filter(hasValue).length
    const extraLabel = extraCount > 0 ? `，额外价格项 ${extraCount}` : ''
    return `输入 $${model.inputPrice}${extraLabel}`
  }
  return '未设置价格'
}

export const buildOptionalFieldToggles = (model: ModelPricingState) => ({
  completionPrice: model.completionRatioLocked || hasValue(model.completionPrice),
  cachePrice: hasValue(model.cachePrice),
  createCachePrice: hasValue(model.createCachePrice),
  imagePrice: hasValue(model.imagePrice),
  audioInputPrice: hasValue(model.audioInputPrice),
  audioOutputPrice: hasValue(model.audioOutputPrice),
})

type OptionalField =
  | 'completionPrice'
  | 'cachePrice'
  | 'createCachePrice'
  | 'imagePrice'
  | 'audioInputPrice'
  | 'audioOutputPrice'

const serializeModel = (model: ModelPricingState): Partial<Record<PricingKey, number | null>> => {
  const result: Partial<Record<PricingKey, number | null>> = {
    ModelPrice: null,
    ModelRatio: null,
    CompletionRatio: null,
    CacheRatio: null,
    CreateCacheRatio: null,
    ImageRatio: null,
    AudioRatio: null,
    AudioCompletionRatio: null,
  }

  if (model.billingMode === 'per-request') {
    if (hasValue(model.fixedPrice)) {
      result.ModelPrice = toNormalizedNumber(model.fixedPrice)
    }
    return result
  }

  const inputPrice = toNumberOrNull(model.inputPrice)
  const completionPrice = toNumberOrNull(model.completionPrice)
  const cachePrice = toNumberOrNull(model.cachePrice)
  const createCachePrice = toNumberOrNull(model.createCachePrice)
  const imagePrice = toNumberOrNull(model.imagePrice)
  const audioInputPrice = toNumberOrNull(model.audioInputPrice)
  const audioOutputPrice = toNumberOrNull(model.audioOutputPrice)

  const hasDependentPrice = [
    completionPrice,
    cachePrice,
    createCachePrice,
    imagePrice,
    audioInputPrice,
    audioOutputPrice,
  ].some((value) => value !== null)

  if (inputPrice === null) {
    if (hasDependentPrice) {
      throw new Error(`模型 ${model.name} 缺少输入价格，无法计算补全/缓存/图片/音频价格对应的倍率`)
    }
    if (hasValue(model.rawRatios.modelRatio)) {
      result.ModelRatio = toNormalizedNumber(model.rawRatios.modelRatio)
    }
    if (hasValue(model.rawRatios.completionRatio)) {
      result.CompletionRatio = toNormalizedNumber(model.rawRatios.completionRatio)
    }
    if (hasValue(model.rawRatios.cacheRatio)) {
      result.CacheRatio = toNormalizedNumber(model.rawRatios.cacheRatio)
    }
    if (hasValue(model.rawRatios.createCacheRatio)) {
      result.CreateCacheRatio = toNormalizedNumber(model.rawRatios.createCacheRatio)
    }
    if (hasValue(model.rawRatios.imageRatio)) {
      result.ImageRatio = toNormalizedNumber(model.rawRatios.imageRatio)
    }
    if (hasValue(model.rawRatios.audioRatio)) {
      result.AudioRatio = toNormalizedNumber(model.rawRatios.audioRatio)
    }
    if (hasValue(model.rawRatios.audioCompletionRatio)) {
      result.AudioCompletionRatio = toNormalizedNumber(model.rawRatios.audioCompletionRatio)
    }
    return result
  }

  result.ModelRatio = toNormalizedNumber(inputPrice / 2)

  if (!model.completionRatioLocked && completionPrice !== null) {
    result.CompletionRatio = toNormalizedNumber(completionPrice / inputPrice)
  } else if (model.completionRatioLocked && hasValue(model.rawRatios.completionRatio)) {
    result.CompletionRatio = toNormalizedNumber(model.rawRatios.completionRatio)
  }
  if (cachePrice !== null) result.CacheRatio = toNormalizedNumber(cachePrice / inputPrice)
  if (createCachePrice !== null) {
    result.CreateCacheRatio = toNormalizedNumber(createCachePrice / inputPrice)
  }
  if (imagePrice !== null) result.ImageRatio = toNormalizedNumber(imagePrice / inputPrice)
  if (audioInputPrice !== null) result.AudioRatio = toNormalizedNumber(audioInputPrice / inputPrice)
  if (audioOutputPrice !== null) {
    if (audioInputPrice === null || audioInputPrice === 0) {
      throw new Error(`模型 ${model.name} 缺少音频输入价格，无法计算音频补全倍率`)
    }
    result.AudioCompletionRatio = toNormalizedNumber(audioOutputPrice / audioInputPrice)
  }

  return result
}

export const buildPreviewRows = (model: ModelPricingState | null) => {
  if (!model) return []

  if (model.billingMode === 'per-request') {
    return [
      {
        key: 'ModelPrice',
        label: 'ModelPrice',
        value: hasValue(model.fixedPrice) ? model.fixedPrice : '空',
      },
    ]
  }

  const inputPrice = toNumberOrNull(model.inputPrice)
  if (inputPrice === null) {
    const rawKeyMap: Record<string, keyof ModelPricingState['rawRatios']> = {
      ModelRatio: 'modelRatio',
      CompletionRatio: 'completionRatio',
      CacheRatio: 'cacheRatio',
      CreateCacheRatio: 'createCacheRatio',
      ImageRatio: 'imageRatio',
      AudioRatio: 'audioRatio',
      AudioCompletionRatio: 'audioCompletionRatio',
    }
    return PRICING_KEYS.filter((k) => k !== 'ModelPrice').map((key) => {
      const rawKey = rawKeyMap[key]
      return {
        key,
        label: key,
        value: hasValue(model.rawRatios[rawKey]) ? model.rawRatios[rawKey] : '空',
      }
    })
  }

  const completionPrice = toNumberOrNull(model.completionPrice)
  const cachePrice = toNumberOrNull(model.cachePrice)
  const createCachePrice = toNumberOrNull(model.createCachePrice)
  const imagePrice = toNumberOrNull(model.imagePrice)
  const audioInputPrice = toNumberOrNull(model.audioInputPrice)
  const audioOutputPrice = toNumberOrNull(model.audioOutputPrice)

  return [
    { key: 'ModelRatio', label: 'ModelRatio', value: formatNumber(inputPrice / 2) },
    {
      key: 'CompletionRatio',
      label: 'CompletionRatio',
      value: model.completionRatioLocked
        ? `${model.lockedCompletionRatio || '空'} (后端固定)`
        : completionPrice !== null
          ? formatNumber(completionPrice / inputPrice)
          : '空',
    },
    {
      key: 'CacheRatio',
      label: 'CacheRatio',
      value: cachePrice !== null ? formatNumber(cachePrice / inputPrice) : '空',
    },
    {
      key: 'CreateCacheRatio',
      label: 'CreateCacheRatio',
      value: createCachePrice !== null ? formatNumber(createCachePrice / inputPrice) : '空',
    },
    {
      key: 'ImageRatio',
      label: 'ImageRatio',
      value: imagePrice !== null ? formatNumber(imagePrice / inputPrice) : '空',
    },
    {
      key: 'AudioRatio',
      label: 'AudioRatio',
      value: audioInputPrice !== null ? formatNumber(audioInputPrice / inputPrice) : '空',
    },
    {
      key: 'AudioCompletionRatio',
      label: 'AudioCompletionRatio',
      value:
        audioOutputPrice !== null && audioInputPrice !== null && audioInputPrice !== 0
          ? formatNumber(audioOutputPrice / audioInputPrice)
          : '空',
    },
  ]
}

export function useModelPricingEditorState({
  options,
  candidateModelNames = EMPTY_CANDIDATE_MODEL_NAMES,
  filterMode = 'all' as FilterMode,
}: {
  options: OptionsAPI
  candidateModelNames?: string[]
  filterMode?: FilterMode
}) {
  const [models, setModels] = useState<ModelPricingState[]>([])
  const [initialVisibleModelNames, setInitialVisibleModelNames] = useState<string[]>([])
  const [selectedModelName, setSelectedModelName] = useState('')
  const [selectedModelNames, setSelectedModelNames] = useState<string[]>([])
  const [searchText, setSearchText] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [loading, setLoading] = useState(false)
  const [conflictOnly, setConflictOnly] = useState(false)
  const [optionalFieldToggles, setOptionalFieldToggles] = useState<
    Record<string, ReturnType<typeof buildOptionalFieldToggles>>
  >({})
  const [parseErrors, setParseErrors] = useState<Record<string, string>>({})

  const hasParseErrors = useMemo(() => Object.keys(parseErrors).length > 0, [parseErrors])
  const canSave = !hasParseErrors

  useEffect(() => {
    const nextParseErrors: Record<string, string> = {}
    const sourceMaps: Record<string, Record<string, unknown>> = {}

    PARSE_OPTION_KEYS.forEach((key) => {
      const result = parseOptionJSONSafe(options.values[key] ?? '')
      if (result.error) {
        nextParseErrors[key] = result.error
      }
      sourceMaps[key] = result.error ? {} : result.value
    })

    setParseErrors(nextParseErrors)

    const names = new Set<string>([
      ...candidateModelNames,
      ...Object.keys(sourceMaps.ModelPrice),
      ...Object.keys(sourceMaps.ModelRatio),
      ...Object.keys(sourceMaps.CompletionRatio),
      ...Object.keys(sourceMaps.CompletionRatioMeta),
      ...Object.keys(sourceMaps.CacheRatio),
      ...Object.keys(sourceMaps.CreateCacheRatio),
      ...Object.keys(sourceMaps.ImageRatio),
      ...Object.keys(sourceMaps.AudioRatio),
      ...Object.keys(sourceMaps.AudioCompletionRatio),
    ])

    const nextModels = Array.from(names)
      .map((name) => buildModelState(name, sourceMaps))
      .sort((a, b) => a.name.localeCompare(b.name))

    setModels(nextModels)
    setInitialVisibleModelNames(
      filterMode === 'unset'
        ? nextModels.filter((model) => isBasePricingUnset(model)).map((model) => model.name)
        : nextModels.map((model) => model.name)
    )
    setOptionalFieldToggles(
      nextModels.reduce<Record<string, ReturnType<typeof buildOptionalFieldToggles>>>((acc, model) => {
        acc[model.name] = buildOptionalFieldToggles(model)
        return acc
      }, {})
    )
    setSelectedModelName((previous) => {
      if (previous && nextModels.some((model) => model.name === previous)) return previous
      const nextVisible =
        filterMode === 'unset'
          ? nextModels.filter((model) => isBasePricingUnset(model))
          : nextModels
      return nextVisible[0]?.name || ''
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidateModelNames, filterMode, options.dataRevision])

  const visibleModels = useMemo(
    () =>
      filterMode === 'unset'
        ? models.filter((model) => initialVisibleModelNames.includes(model.name))
        : models,
    [filterMode, initialVisibleModelNames, models]
  )

  const filteredModels = useMemo(
    () =>
      visibleModels.filter((model) => {
        const keyword = searchText.trim().toLowerCase()
        const keywordMatch = keyword ? model.name.toLowerCase().includes(keyword) : true
        const conflictMatch = conflictOnly ? model.hasConflict : true
        return keywordMatch && conflictMatch
      }),
    [conflictOnly, searchText, visibleModels]
  )

  const pagedData = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE
    return filteredModels.slice(start, start + PAGE_SIZE)
  }, [currentPage, filteredModels])

  const selectedModel = useMemo(
    () => visibleModels.find((model) => model.name === selectedModelName) || null,
    [selectedModelName, visibleModels]
  )

  const selectedWarnings = useMemo(() => getModelWarnings(selectedModel), [selectedModel])
  const previewRows = useMemo(() => buildPreviewRows(selectedModel), [selectedModel])

  useEffect(() => {
    setCurrentPage(1)
  }, [searchText, conflictOnly, filterMode, candidateModelNames])

  useEffect(() => {
    setSelectedModelNames((previous) =>
      previous.filter((name) => visibleModels.some((model) => model.name === name))
    )
  }, [visibleModels])

  useEffect(() => {
    if (visibleModels.length === 0) {
      setSelectedModelName('')
      return
    }
    if (!visibleModels.some((model) => model.name === selectedModelName)) {
      setSelectedModelName(visibleModels[0].name)
    }
  }, [selectedModelName, visibleModels])

  const upsertModel = (name: string, updater: ModelPricingState | ((model: ModelPricingState) => ModelPricingState)) => {
    setModels((previous) =>
      previous.map((model) => {
        if (model.name !== name) return model
        return typeof updater === 'function' ? updater(model) : updater
      })
    )
  }

  const isOptionalFieldEnabled = (model: ModelPricingState | null, field: OptionalField) => {
    if (!model) return false
    const modelToggles = optionalFieldToggles[model.name]
    if (modelToggles && typeof modelToggles[field] === 'boolean') return modelToggles[field]
    return buildOptionalFieldToggles(model)[field]
  }

  const updateOptionalFieldToggle = (modelName: string, field: OptionalField, checked: boolean) => {
    setOptionalFieldToggles((prev) => ({
      ...prev,
      [modelName]: {
        ...(prev[modelName] || {}),
        [field]: checked,
      },
    }))
  }

  const handleOptionalFieldToggle = (field: OptionalField, checked: boolean) => {
    if (!selectedModel) return
    updateOptionalFieldToggle(selectedModel.name, field, checked)
    if (checked) return

    upsertModel(selectedModel.name, (model) => {
      const nextModel = { ...model, [field]: '' }
      if (field === 'audioInputPrice') {
        nextModel.audioOutputPrice = ''
        setOptionalFieldToggles((prev) => ({
          ...prev,
          [selectedModel.name]: {
            ...(prev[selectedModel.name] || {}),
            audioInputPrice: false,
            audioOutputPrice: false,
          },
        }))
      }
      return nextModel
    })
  }

  const fillDerivedPricesFromBase = (model: ModelPricingState, nextInputPrice: string) => {
    const baseNumber = toNumberOrNull(nextInputPrice)
    if (baseNumber === null) return model

    return {
      ...model,
      completionPrice:
        model.completionRatioLocked && hasValue(model.lockedCompletionRatio)
          ? formatNumber(baseNumber * Number(model.lockedCompletionRatio))
          : !hasValue(model.completionPrice) && hasValue(model.rawRatios.completionRatio)
            ? formatNumber(baseNumber * Number(model.rawRatios.completionRatio))
            : model.completionPrice,
      cachePrice:
        !hasValue(model.cachePrice) && hasValue(model.rawRatios.cacheRatio)
          ? formatNumber(baseNumber * Number(model.rawRatios.cacheRatio))
          : model.cachePrice,
      createCachePrice:
        !hasValue(model.createCachePrice) && hasValue(model.rawRatios.createCacheRatio)
          ? formatNumber(baseNumber * Number(model.rawRatios.createCacheRatio))
          : model.createCachePrice,
      imagePrice:
        !hasValue(model.imagePrice) && hasValue(model.rawRatios.imageRatio)
          ? formatNumber(baseNumber * Number(model.rawRatios.imageRatio))
          : model.imagePrice,
      audioInputPrice:
        !hasValue(model.audioInputPrice) && hasValue(model.rawRatios.audioRatio)
          ? formatNumber(baseNumber * Number(model.rawRatios.audioRatio))
          : model.audioInputPrice,
      audioOutputPrice:
        !hasValue(model.audioOutputPrice) &&
        hasValue(model.rawRatios.audioRatio) &&
        hasValue(model.rawRatios.audioCompletionRatio)
          ? formatNumber(
              baseNumber * Number(model.rawRatios.audioRatio) * Number(model.rawRatios.audioCompletionRatio)
            )
          : model.audioOutputPrice,
    }
  }

  const handleNumericFieldChange = (field: keyof ModelPricingState, value: string) => {
    if (!selectedModel || !NUMERIC_INPUT_REGEX.test(value)) return
    upsertModel(selectedModel.name, (model) => {
      const updatedModel = { ...model, [field]: value }
      if (field === 'inputPrice') return fillDerivedPricesFromBase(updatedModel, value)
      return updatedModel
    })
  }

  const handleBillingModeChange = (value: 'per-token' | 'per-request') => {
    if (!selectedModel) return
    upsertModel(selectedModel.name, (model) => {
      if (value === 'per-request') {
        return {
          ...model,
          billingMode: value,
          inputPrice: '',
          completionPrice: '',
          cachePrice: '',
          createCachePrice: '',
          imagePrice: '',
          audioInputPrice: '',
          audioOutputPrice: '',
          rawRatios: { ...EMPTY_MODEL.rawRatios },
          hasConflict: false,
        }
      }
      return {
        ...model,
        billingMode: value,
        fixedPrice: '',
        hasConflict: false,
      }
    })
    setOptionalFieldToggles((prev) => ({
      ...prev,
      [selectedModel.name]: buildOptionalFieldToggles({
        ...selectedModel,
        billingMode: value,
        fixedPrice: value === 'per-token' ? '' : selectedModel.fixedPrice,
        inputPrice: value === 'per-request' ? '' : selectedModel.inputPrice,
      }),
    }))
  }

  const addModel = (modelName: string) => {
    const trimmedName = modelName.trim()
    if (!trimmedName) {
      Message.error('请输入模型名称')
      return false
    }
    if (models.some((model) => model.name === trimmedName)) {
      Message.error('模型名称已存在')
      return false
    }
    const nextModel: ModelPricingState = {
      ...EMPTY_MODEL,
      name: trimmedName,
      rawRatios: { ...EMPTY_MODEL.rawRatios },
    }
    setModels((previous) => [nextModel, ...previous])
    setOptionalFieldToggles((prev) => ({ ...prev, [trimmedName]: buildOptionalFieldToggles(nextModel) }))
    setSelectedModelName(trimmedName)
    setCurrentPage(1)
    return true
  }

  const deleteModel = (name: string) => {
    const nextModels = models.filter((model) => model.name !== name)
    setModels(nextModels)
    setOptionalFieldToggles((prev) => {
      const next = { ...prev }
      delete next[name]
      return next
    })
    setSelectedModelNames((previous) => previous.filter((item) => item !== name))
    if (selectedModelName === name) setSelectedModelName(nextModels[0]?.name || '')
  }

  const applySelectedModelPricing = () => {
    if (!selectedModel) {
      Message.error('请先选择一个作为模板的模型')
      return false
    }
    if (selectedModelNames.length === 0) {
      Message.error('请先勾选需要批量设置的模型')
      return false
    }

    const sourceToggles = optionalFieldToggles[selectedModel.name] || {}

    setModels((previous) =>
      previous.map((model) => {
        if (!selectedModelNames.includes(model.name)) return model
        const nextModel = {
          ...model,
          billingMode: selectedModel.billingMode,
          fixedPrice: selectedModel.fixedPrice,
          inputPrice: selectedModel.inputPrice,
          completionPrice: selectedModel.completionPrice,
          cachePrice: selectedModel.cachePrice,
          createCachePrice: selectedModel.createCachePrice,
          imagePrice: selectedModel.imagePrice,
          audioInputPrice: selectedModel.audioInputPrice,
          audioOutputPrice: selectedModel.audioOutputPrice,
        }
        if (
          nextModel.billingMode === 'per-token' &&
          nextModel.completionRatioLocked &&
          hasValue(nextModel.inputPrice) &&
          hasValue(nextModel.lockedCompletionRatio)
        ) {
          nextModel.completionPrice = formatNumber(
            Number(nextModel.inputPrice) * Number(nextModel.lockedCompletionRatio)
          )
        }
        return nextModel
      })
    )

    setOptionalFieldToggles((previous) => {
      const next = { ...previous }
      selectedModelNames.forEach((modelName) => {
        const targetModel = models.find((item) => item.name === modelName)
        next[modelName] = {
          completionPrice: targetModel?.completionRatioLocked
            ? true
            : Boolean(sourceToggles.completionPrice),
          cachePrice: Boolean(sourceToggles.cachePrice),
          createCachePrice: Boolean(sourceToggles.createCachePrice),
          imagePrice: Boolean(sourceToggles.imagePrice),
          audioInputPrice: Boolean(sourceToggles.audioInputPrice),
          audioOutputPrice: Boolean(sourceToggles.audioInputPrice) && Boolean(sourceToggles.audioOutputPrice),
        }
      })
      return next
    })

    Message.success(`已将模型 ${selectedModel.name} 的价格配置批量应用到 ${selectedModelNames.length} 个模型`)
    return true
  }

  const handleSubmit = useCallback(async () => {
    if (hasParseErrors) {
      Message.error('存在无法解析的定价 JSON，请先在「模型倍率设置」高级 Tab 修复后再保存')
      return
    }

    setLoading(true)
    try {
      const output: Record<PricingKey, Record<string, number>> = {
        ModelPrice: {},
        ModelRatio: {},
        CompletionRatio: {},
        CacheRatio: {},
        CreateCacheRatio: {},
        ImageRatio: {},
        AudioRatio: {},
        AudioCompletionRatio: {},
      }

      for (const model of models) {
        const serialized = serializeModel(model)
        ;(Object.entries(serialized) as Array<[PricingKey, number | null]>).forEach(([key, value]) => {
          if (value !== null) output[key][model.name] = value
        })
      }

      const overrides = PRICING_KEYS.reduce<Record<string, string>>((acc, key) => {
        acc[key] = JSON.stringify(output[key], null, 2)
        return acc
      }, {})

      await options.save([...PRICING_KEYS], overrides)
      Message.success('保存成功')
      await options.reload()
    } catch (error) {
      Message.error(error instanceof Error ? error.message : '保存失败，请重试')
    } finally {
      setLoading(false)
    }
  }, [hasParseErrors, models, options])

  return {
    models,
    selectedModel,
    selectedModelName,
    selectedModelNames,
    setSelectedModelName,
    setSelectedModelNames,
    searchText,
    setSearchText,
    currentPage,
    setCurrentPage,
    loading,
    parseErrors,
    hasParseErrors,
    canSave,
    conflictOnly,
    setConflictOnly,
    filteredModels,
    pagedData,
    selectedWarnings,
    previewRows,
    isOptionalFieldEnabled,
    handleOptionalFieldToggle,
    handleNumericFieldChange,
    handleBillingModeChange,
    handleSubmit,
    addModel,
    deleteModel,
    applySelectedModelPricing,
  }
}
